import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Input, Modal } from '@bruno/ui';
import type { PlanDTO, PlanInput } from '@bruno/shared';
import {
  adminArchivePlan,
  adminCreatePlan,
  adminListPlans,
  adminSyncPlan,
  adminUpdatePlan,
} from '../api/admin';

const empty: PlanInput = {
  name: '',
  description: '',
  monthlyPriceCents: 0,
  maxConcurrentUsers: 1,
  maxKnowledgeMb: 25,
  maxAgents: 0,
  includedTokens: 0,
  features: {
    hidePoweredBy: false,
    customBrand: false,
    systemPromptOverride: false,
  },
};

interface KnownFeatures {
  hidePoweredBy: boolean;
  customBrand: boolean;
  systemPromptOverride: boolean;
}

const KNOWN_FEATURE_KEYS: ReadonlyArray<keyof KnownFeatures> = [
  'hidePoweredBy',
  'customBrand',
  'systemPromptOverride',
];

function splitFeatures(input: Record<string, unknown> | undefined): {
  known: KnownFeatures;
  extras: Record<string, unknown>;
} {
  const f = input ?? {};
  const known: KnownFeatures = {
    hidePoweredBy: f.hidePoweredBy === true,
    customBrand: f.customBrand === true,
    systemPromptOverride: f.systemPromptOverride === true,
  };
  const extras: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f)) {
    if (!(KNOWN_FEATURE_KEYS as readonly string[]).includes(k)) extras[k] = v;
  }
  return { known, extras };
}

export function PlansPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin-plans'], queryFn: adminListPlans });
  const [editing, setEditing] = useState<PlanDTO | null>(null);
  const [open, setOpen] = useState(false);

  const create = useMutation({
    mutationFn: adminCreatePlan,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-plans'] });
      setOpen(false);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: PlanInput }) => adminUpdatePlan(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-plans'] });
      setOpen(false);
      setEditing(null);
    },
  });
  const archive = useMutation({
    mutationFn: adminArchivePlan,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-plans'] }),
  });
  const sync = useMutation({
    mutationFn: adminSyncPlan,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-plans'] }),
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Plans</h1>
        <Button onClick={() => { setEditing(null); setOpen(true); }}>+ New plan</Button>
      </div>
      {isLoading ? (
        <div className="text-slate-500">Loading...</div>
      ) : (
        <div className="grid gap-3">
          {(data?.plans ?? []).map((p) => {
            const f = (p.features as Record<string, unknown> | undefined) ?? {};
            return (
            <Card key={p.id}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center flex-wrap gap-2">
                    <span className="font-semibold">{p.name}</span>
                    {p.archived ? <Badge tone="gray">archived</Badge> : null}
                    {p.stripePriceId ? <Badge tone="green">stripe synced</Badge> : <Badge tone="amber">unsynced</Badge>}
                    {f.hidePoweredBy === true ? <Badge tone="indigo">hide powered-by</Badge> : null}
                    {f.customBrand === true ? <Badge tone="indigo">custom brand</Badge> : null}
                    {f.systemPromptOverride === true ? <Badge tone="indigo">prompt override</Badge> : null}
                  </div>
                  {p.description ? <p className="text-sm text-slate-500">{p.description}</p> : null}
                  <div className="text-sm text-slate-600 mt-1">
                    ${(p.monthlyPriceCents / 100).toFixed(2)}/mo · {p.maxConcurrentUsers} concurrent · {p.includedTokens.toLocaleString()} tokens · {p.maxKnowledgeMb} MB · {p.maxAgents ?? 0} agent{p.maxAgents === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Button size="sm" variant="secondary" onClick={() => { setEditing(p); setOpen(true); }}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => sync.mutate(p.id)} loading={sync.isPending}>
                    Sync to Stripe
                  </Button>
                  {!p.archived ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (confirm('Archive this plan?')) archive.mutate(p.id);
                      }}
                    >
                      Archive
                    </Button>
                  ) : null}
                </div>
              </div>
            </Card>
            );
          })}
        </div>
      )}

      <PlanFormModal
        open={open}
        onClose={() => { setOpen(false); setEditing(null); }}
        initial={editing}
        onSubmit={(values) => {
          if (editing) update.mutate({ id: editing.id, body: values });
          else create.mutate(values);
        }}
        loading={create.isPending || update.isPending}
      />
    </div>
  );
}

function PlanFormModal({
  open,
  onClose,
  initial,
  onSubmit,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  initial: PlanDTO | null;
  onSubmit: (values: PlanInput) => void;
  loading: boolean;
}) {
  const [v, setV] = useState<PlanInput>(initial ? planFromDto(initial) : empty);
  const initialFeatures = initial?.features ?? empty.features ?? {};
  const initialSplit = splitFeatures(initialFeatures as Record<string, unknown>);
  const [features, setFeatures] = useState<KnownFeatures>(initialSplit.known);
  const [extrasText, setExtrasText] = useState<string>(
    Object.keys(initialSplit.extras).length ? JSON.stringify(initialSplit.extras, null, 2) : '',
  );
  const [extrasError, setExtrasError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setV(initial ? planFromDto(initial) : empty);
    const split = splitFeatures(((initial?.features ?? empty.features) ?? {}) as Record<string, unknown>);
    setFeatures(split.known);
    setExtrasText(Object.keys(split.extras).length ? JSON.stringify(split.extras, null, 2) : '');
    setExtrasError(null);
  }, [initial, open]);

  function submit(e: FormEvent) {
    e.preventDefault();
    let extras: Record<string, unknown> = {};
    if (extrasText.trim()) {
      try {
        const parsed = JSON.parse(extrasText);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          throw new Error('must be a JSON object');
        }
        extras = parsed as Record<string, unknown>;
      } catch (err) {
        setExtrasError(err instanceof Error ? err.message : 'invalid JSON');
        return;
      }
    }
    setExtrasError(null);
    onSubmit({ ...v, features: { ...extras, ...features } });
  }

  return (
    <Modal open={open} onClose={onClose} title={initial ? 'Edit plan' : 'New plan'}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Input label="Name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} required />
        <Input
          label="Description"
          value={v.description ?? ''}
          onChange={(e) => setV({ ...v, description: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Price (USD cents/month)"
            type="number"
            value={String(v.monthlyPriceCents)}
            onChange={(e) => setV({ ...v, monthlyPriceCents: Number(e.target.value) })}
          />
          <Input
            label="Max concurrent end-users"
            type="number"
            value={String(v.maxConcurrentUsers)}
            onChange={(e) => setV({ ...v, maxConcurrentUsers: Number(e.target.value) })}
          />
          <Input
            label="Max knowledge MB"
            type="number"
            value={String(v.maxKnowledgeMb)}
            onChange={(e) => setV({ ...v, maxKnowledgeMb: Number(e.target.value) })}
          />
          <Input
            label="Included tokens / mo"
            type="number"
            value={String(v.includedTokens)}
            onChange={(e) => setV({ ...v, includedTokens: Number(e.target.value) })}
          />
          <Input
            label="Max human agents"
            type="number"
            value={String(v.maxAgents)}
            onChange={(e) => setV({ ...v, maxAgents: Number(e.target.value) })}
          />
        </div>

        <div className="border-t border-slate-200 pt-3">
          <div className="text-sm font-medium text-slate-700 mb-2">Features</div>
          <div className="flex flex-col gap-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={features.hidePoweredBy}
                onChange={(e) => setFeatures({ ...features, hidePoweredBy: e.target.checked })}
              />
              <span>Allow hiding the &quot;Powered by Bruno&quot; footer</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={features.customBrand}
                onChange={(e) => setFeatures({ ...features, customBrand: e.target.checked })}
              />
              <span>Allow custom brand name and accent color</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={features.systemPromptOverride}
                onChange={(e) => setFeatures({ ...features, systemPromptOverride: e.target.checked })}
              />
              <span>Allow custom system prompt override</span>
            </label>
          </div>
          <div className="mt-3">
            <label className="text-xs font-medium text-slate-700" htmlFor="plan-extra-features">
              Other features (advanced, JSON object - optional)
            </label>
            <textarea
              id="plan-extra-features"
              className="mt-1 min-h-[80px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              placeholder='{ "anyKey": true }'
              value={extrasText}
              onChange={(e) => setExtrasText(e.target.value)}
            />
            {extrasError ? <p className="text-xs text-red-600 mt-1">{extrasError}</p> : null}
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={loading}>{initial ? 'Save' : 'Create'}</Button>
        </div>
      </form>
    </Modal>
  );
}

function planFromDto(p: PlanDTO): PlanInput {
  return {
    name: p.name,
    description: p.description ?? '',
    monthlyPriceCents: p.monthlyPriceCents,
    maxConcurrentUsers: p.maxConcurrentUsers,
    maxKnowledgeMb: p.maxKnowledgeMb,
    maxAgents: p.maxAgents ?? 0,
    includedTokens: p.includedTokens,
    features: p.features,
    archived: p.archived,
  };
}
